import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useTranslation, useLocale } from "./contexts/LocaleContext";
import { setAccessToken, setUserData } from "./utils/auth";
import { CheckCircle, XCircle, Loader, Mail } from "lucide-react";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const { lang } = useParams();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const t = useTranslation();

  // Helper to get localized link
  const getLocalizedLink = (path: string) => {
    const cleanPath = path.startsWith("/") ? path.substring(1) : path;
    if (lang && lang !== "cs") {
      return `/${lang}/${cleanPath}`;
    }
    return `/${cleanPath}`;
  };

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [message, setMessage] = useState<string>("");
  const verifyCalled = React.useRef(false);
  const token = searchParams.get("token");
  const success = searchParams.get("success");
  const error = searchParams.get("error");

  useEffect(() => {
    // If token is provided, verify via POST
    if (token) {
      if (verifyCalled.current) return;
      verifyCalled.current = true;
      verifyEmail(token);
    }
    // If success/error from GET redirect
    else if (success === "true") {
      setStatus("success");
      setMessage(t.landing.verifyEmail.success);
      // Check if token is in URL (from GET redirect)
      const tokenParam = searchParams.get("token");
      if (tokenParam) {
        // Token is JWT access token, save it
        setAccessToken(tokenParam);
        setTimeout(() => {
          navigate(getLocalizedLink("/app"));
        }, 2000);
      }
    } else if (error) {
      setStatus("error");
      setMessage(decodeURIComponent(error));
    } else {
      setStatus("error");
      setMessage(t.landing.verifyEmail.noToken);
    }
  }, [token, success, error]);

  const verifyEmail = async (verificationToken: string) => {
    try {
      const response = await fetch("/auth/verify-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: verificationToken }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus("success");
        setMessage(data.message || t.landing.verifyEmail.success);
        // If accessToken is provided, save it and redirect
        if (data.accessToken) {
          setAccessToken(data.accessToken);
          if (data.user) setUserData(data.user);
          // Redirect after a short delay to show success message
          setTimeout(() => {
            navigate(getLocalizedLink("/app"));
          }, 2000);
        }
      } else {
        // Handle "User already exists" as success (soft landing)
        if (response.status === 409 || data.error === "User already exists") {
          setStatus("success");
          setMessage(t.landing.verifyEmail.alreadyVerified);
          setTimeout(() => {
            navigate(getLocalizedLink("/login"));
          }, 2000);
          return;
        }

        setStatus("error");
        const errorCode = data.error as keyof typeof t.landing.auth.errors;
        setMessage(
          t.landing.auth.errors[errorCode] ||
            data.error ||
            t.landing.verifyEmail.failed
        );
      }
    } catch (err) {
      setStatus("error");
      setMessage(t.landing.verifyEmail.failed);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-950 via-slate-950 to-black px-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 text-center">
        {status === "loading" && (
          <>
            <Loader className="h-16 w-16 mx-auto mb-4 text-indigo-600 animate-spin" />
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              {t.landing.verifyEmail.verifying}
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              {t.landing.verifyEmail.wait}
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              {t.landing.verifyEmail.verified}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{message}</p>
            <button
              onClick={() => navigate(getLocalizedLink("/app"))}
              className="w-full px-4 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-sm transition-all shadow-sm border-0"
            >
              {t.landing.verifyEmail.goDashboard}
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              {t.landing.verifyEmail.failed}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{message}</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate(getLocalizedLink("/login"))}
                className="w-full px-4 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-sm transition-all shadow-sm border-0"
              >
                {t.landing.verifyEmail.goLogin}
              </button>
              <button
                onClick={() => navigate(getLocalizedLink("/signup"))}
                className="w-full px-4 py-2.5 rounded-xl bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100 hover:bg-slate-300 dark:hover:bg-slate-600 font-semibold text-sm transition-all border-0"
              >
                {t.landing.verifyEmail.signUpAgain}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
